import { invariant } from '../util/invariant';
import type { Deferred } from './async';
import { defer } from './async';

/**
 * Warning: The purpose of the Coalesce.runOrCoalesce() instance is to avoid performing
 * needless tasks in a series of equivalent tasks, where only the result of the last task to
 * be executed matters. By equivalent, we mean that the functions are intended to
 * achieve the same goal. An example of such a goal would be to obtain the latest state
 * from some specific external resource. Do not call runOrCoalesce() run single Coalesce
 * instance with a series of non-equivalent tasks, or when you require every tasks to
 * always be run. If you have multiple groups of equivalent tasks, then you can create
 * one Coalesce instance for each group. see: https://github.com/Mjoll/mimir/issues/9343
 *
 * Executes task immediately, if no task is currently running. Otherwise queues
 * the task in a batch. Once the currently running task finishes, then the
 * latest queued task in the batch will be run, and its result will be returned
 * for all the tasks in the batch.
 *
 * This is similar to Lodash debounce, except it uses the execution time of
 * asynchronous tasks for timing, rather than a fixed time delay.
 *
 * It is also similar to coalesceTasks() except that it is not an
 * AsyncGenerator, and therefore does not need an input generator which knows
 * when the last task has been seen.
 */
export class Coalesce<T> {
  running: boolean = false;
  queued: QueuedTask<T>[] = [];

  async runOrCoalesce(task: () => Promise<T>) {
    const promiseSource = defer<T>();
    this.queued.push({
      deferred: promiseSource.deferred,
      task
    });

    this.tryStartBatch();
    return promiseSource.promise;
  }

  private tryStartBatch() {
    if (this.running || this.queued.length == 0) {
      return;
    }

    const batch = this.queued;
    this.queued = [];
    this.runLatestFromBatch(batch).catch((ex: unknown) => console.error('Coalesce', ex));
  }

  private async runLatestFromBatch(batch:  QueuedTask<T>[]) {
    invariant(batch.length > 0, 'Expected at least one batched task');
    invariant(!this.running, 'Expected no batch to be running.');
    const lastTask = batch[batch.length - 1]!;
    const promise = lastTask.task();
    this.running = true;
    try {
      const result = await promise;
      for (const qt of batch) {
        try {
          qt.deferred.resolve(result);
        } catch (ex: unknown) {
          console.warn('runLatestFromBatch, resolve failed', ex);
        }
      }
    } catch (cause: unknown) {
      for (const qt of batch) {
        try {
          qt.deferred.reject(cause);
        } catch (ex: unknown) {
          console.warn('runLatestFromBatch, reject failed', ex);
        }
      }
    } finally {
      this.running = false;
    }

    // Process next batch if any.
    this.tryStartBatch();
  }
}

interface QueuedTask<T> {
  deferred: Deferred<T>;
  task: () => Promise<T>
}
